import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Check,
  Trash2,
  Clock,
  Users,
  CheckCircle2,
  AlertCircle,
  GripVertical,
  RefreshCw,
} from 'lucide-react';
import {
  ADD_VISITS,
  GET_CUSTOM_RETAILERS,
  GET_FSRS,
  GET_RETAILERS,
  GET_VISITS,
  GET_VISIT_DAY_PROGRESS,
  GET_VISIT_DAY_STATS,
  MARK_VISIT_DONE,
  REMOVE_VISIT,
  REORDER_VISITS,
  UPDATE_VISIT,
} from '../graphql/queries';
import { SlidePanel } from '../components/SlidePanel';
import { Modal } from '../components/Modal';
import { useMyStore } from '../hooks/useMyStore';

// ── Date helpers ──────────────────────────────────────────────────
const toIso = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const addDays = (d: Date, n: number) => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};
const startOfWeek = (d: Date) => {
  const c = new Date(d);
  const day = c.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday-based
  c.setDate(c.getDate() + diff);
  c.setHours(0, 0, 0, 0);
  return c;
};
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// ── Types ─────────────────────────────────────────────────────────
interface LiveFsr {
  id: string;
  name: string;
  employeeId: string;
  avatar: string | null;
  color: string | null;
  assignedRetailerIds: string[];
}

interface Visit {
  id: string;
  fsrId: string;
  date: string;
  retailerId: string;
  retailerName: string | null;
  retailerCity: string | null;
  retailerMobile: string | null;
  priority: 'high' | 'medium' | 'low';
  scheduledTime: string | null;
  sequence: number;
  status: 'pending' | 'visiting' | 'done' | 'skipped';
}

interface ConnectorRetailer {
  id: string;
  displayName: string | null;
  companyName: string | null;
  city: string | null;
  email: string | null;
  mobile: string | null;
}

const initialsOf = (name?: string | null) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const STATUS_PILL = {
  pending: 'bg-gray-100 text-gray-700',
  visiting: 'bg-amber-100 text-amber-700',
  done: 'bg-emerald-100 text-emerald-700',
  skipped: 'bg-red-100 text-red-700',
} as const;

const STATUS_DOT = {
  pending: 'bg-gray-400',
  visiting: 'bg-amber-500',
  done: 'bg-emerald-500',
  skipped: 'bg-red-500',
} as const;

// ── Page ──────────────────────────────────────────────────────────
export const VisitScheduler: React.FC = () => {
  const [date, setDate] = useState<Date>(new Date());
  const isoDate = toIso(date);
  const [selectedFsrId, setSelectedFsrId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showAddPanel, setShowAddPanel] = useState(false);

  const showToast = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 2200);
  };

  const fsrQuery = useQuery(GET_FSRS, {
    variables: { page: 1, limit: 200, status: 'active' },
    fetchPolicy: 'cache-and-network',
  });
  const fsrs: LiveFsr[] = (fsrQuery.data?.getFsrs?.fsrs ?? []).map((f: any) => ({
    id: f.id,
    name: f.name,
    employeeId: f.employeeId,
    avatar: f.avatar,
    color: f.color,
    assignedRetailerIds: (f.assignedRetailers ?? []).map((a: any) => a.retailerId),
  }));

  useEffect(() => {
    if (!selectedFsrId && fsrs.length > 0) setSelectedFsrId(fsrs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fsrs.length]);

  const statsQuery = useQuery(GET_VISIT_DAY_STATS, {
    variables: { date: isoDate },
    fetchPolicy: 'cache-and-network',
  });
  const stats = statsQuery.data?.getVisitDayStats ?? {
    fsrsWithPlan: 0,
    storesScheduled: 0,
    completed: 0,
    pending: 0,
    skipped: 0,
  };

  const progressQuery = useQuery(GET_VISIT_DAY_PROGRESS, {
    variables: { date: isoDate },
    fetchPolicy: 'cache-and-network',
  });
  const progressByFsr = useMemo(() => {
    const m: Record<string, { total: number; done: number; pending: number }> = {};
    (progressQuery.data?.getVisitDayProgress ?? []).forEach((p: any) => {
      m[p.fsrId] = { total: p.total, done: p.done, pending: p.pending };
    });
    return m;
  }, [progressQuery.data]);

  const visitsQuery = useQuery(GET_VISITS, {
    variables: { fsrId: selectedFsrId ?? '', date: isoDate },
    skip: !selectedFsrId,
    fetchPolicy: 'cache-and-network',
  });
  const visits: Visit[] = visitsQuery.data?.getVisits?.visits ?? [];

  const refreshAll = async () => {
    await Promise.all([statsQuery.refetch(), progressQuery.refetch(), visitsQuery.refetch()]);
  };

  const [addVisits, { loading: adding }] = useMutation(ADD_VISITS, { onCompleted: refreshAll });
  const [markDone] = useMutation(MARK_VISIT_DONE, { onCompleted: refreshAll });
  const [removeVisit] = useMutation(REMOVE_VISIT, { onCompleted: refreshAll });
  const [updateVisit] = useMutation(UPDATE_VISIT, { onCompleted: refreshAll });
  const [reorderVisits] = useMutation(REORDER_VISITS, { onCompleted: refreshAll });

  const [pendingDone, setPendingDone] = useState<Visit | null>(null);

  // ── Add Stores panel state (lifted up so the SlidePanel footer can render the submit button) ──
  const [pickedRetailerIds, setPickedRetailerIds] = useState<string[]>([]);
  const [pickedTime, setPickedTime] = useState<string>('');

  // Reset panel state every time it opens / closes
  useEffect(() => {
    if (!showAddPanel) {
      setPickedRetailerIds([]);
      setPickedTime('');
    }
  }, [showAddPanel]);

  // Drag-reorder via HTML5 drag/drop (no extra lib).
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [orderedIds, setOrderedIds] = useState<string[] | null>(null);

  useEffect(() => {
    setOrderedIds(null);
  }, [visits.length, isoDate, selectedFsrId]);

  const displayedVisits = orderedIds
    ? (orderedIds.map((id) => visits.find((v) => v.id === id)).filter(Boolean) as Visit[])
    : visits;

  const onDragStart = (id: string) => setDraggingId(id);
  const onDragOver = (e: React.DragEvent, overId: string) => {
    e.preventDefault();
    if (!draggingId || draggingId === overId) return;
    const baseOrder = orderedIds ?? visits.map((v) => v.id);
    const fromIdx = baseOrder.indexOf(draggingId);
    const toIdx = baseOrder.indexOf(overId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...baseOrder];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, draggingId);
    setOrderedIds(next);
  };
  const onDragEnd = async () => {
    setDraggingId(null);
    if (!orderedIds || !selectedFsrId) return;
    await reorderVisits({ variables: { fsrId: selectedFsrId, date: isoDate, orderedIds } });
    showToast('Plan reordered');
  };

  const askMarkDone = (visit: Visit) => setPendingDone(visit);
  const confirmMarkDone = async () => {
    if (!pendingDone) return;
    await markDone({ variables: { id: pendingDone.id } });
    setPendingDone(null);
    showToast('Visit marked done');
  };
  const handleRemove = async (id: string) => {
    await removeVisit({ variables: { id } });
    showToast('Visit removed');
  };
  const handleTimeChange = async (id: string, scheduledTime: string) => {
    await updateVisit({ variables: { id, input: { scheduledTime } } });
  };
  const handleAddSubmit = async () => {
    if (!selectedFsrId || pickedRetailerIds.length === 0) return;
    const { data } = await addVisits({
      variables: {
        fsrId: selectedFsrId,
        date: isoDate,
        retailerIds: pickedRetailerIds,
        scheduledTime: pickedTime || null,
      },
    });
    const out = data?.addVisits;
    if (out?.error) {
      showToast(out.message || 'Failed to add');
      return;
    }
    showToast(`Added ${out?.visits?.length ?? 0} stores`);
    setShowAddPanel(false);
  };

  const selectedFsr = fsrs.find((f) => f.id === selectedFsrId) ?? null;

  return (
    <div className="space-y-6 page-fade-in pb-10">
      {toast && (
        <div className="fixed top-6 right-6 bg-emerald-600 text-white px-4 py-3 rounded-xl shadow-lg z-50 flex items-center gap-2 text-sm font-semibold">
          <Check size={16} /> {toast}
        </div>
      )}

      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text">Visit Scheduler</h1>
          <p className="text-text2">Plan daily store visits for your field team.</p>
        </div>
        <button
          onClick={refreshAll}
          className="h-10 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2 flex items-center gap-2"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </header>

      <DateNavigator date={date} onChange={setDate} />

      <div className="grid grid-cols-4 gap-4">
        <KpiCard label="FSRs With Plan" value={stats.fsrsWithPlan} icon={Users} grad="from-indigo-600 to-indigo-700" />
        <KpiCard
          label="Stores Scheduled"
          value={stats.storesScheduled}
          icon={CalendarIcon}
          grad="from-blue-600 to-blue-700"
        />
        <KpiCard
          label="Completed"
          value={stats.completed}
          icon={CheckCircle2}
          grad="from-emerald-600 to-emerald-700"
        />
        <KpiCard label="Pending" value={stats.pending} icon={AlertCircle} grad="from-amber-600 to-amber-700" />
      </div>

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-4 card p-4 flex flex-col max-h-[700px]">
          <header className="px-2 pb-3 mb-2 border-b border-border">
            <h2 className="text-sm font-black text-text uppercase tracking-widest flex items-center gap-2">
              <Users size={14} className="text-primary" /> Field Officers
            </h2>
            <p className="text-[11px] text-text3">Click an officer to plan their day.</p>
          </header>
          <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2">
            {fsrQuery.loading && fsrs.length === 0 ? (
              <div className="text-center text-text3 text-sm py-12">Loading FSRs…</div>
            ) : fsrs.length === 0 ? (
              <div className="text-center text-text3 text-sm py-12">No active field users.</div>
            ) : (
              fsrs.map((f) => {
                const isSelected = f.id === selectedFsrId;
                const p = progressByFsr[f.id] ?? { total: 0, done: 0, pending: 0 };
                const pct = p.total > 0 ? Math.round((p.done / p.total) * 100) : 0;
                return (
                  <button
                    key={f.id}
                    onClick={() => setSelectedFsrId(f.id)}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-colors flex items-center gap-3 ${
                      isSelected ? 'bg-indigo-50 border-primary' : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-xs shadow-sm shrink-0"
                      style={{ backgroundColor: f.color || '#4F46E5' }}
                    >
                      {f.avatar || initialsOf(f.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-bold text-text truncate">{f.name}</span>
                        <span className="text-[10px] font-bold text-text3">
                          {p.done}/{p.total}
                        </span>
                      </div>
                      <div className="text-[10px] text-text3 truncate mb-1">{f.employeeId}</div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <main className="col-span-8 card flex flex-col max-h-[700px]">
          {!selectedFsr ? (
            <EmptyState />
          ) : (
            <>
              <header className="px-6 py-5 border-b border-border flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-black text-sm shadow shrink-0"
                    style={{ backgroundColor: selectedFsr.color || '#4F46E5' }}
                  >
                    {selectedFsr.avatar || initialsOf(selectedFsr.name)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-black text-text truncate">{selectedFsr.name}'s Plan</h3>
                    <p className="text-xs text-text3">
                      {visits.length} {visits.length === 1 ? 'store' : 'stores'} on{' '}
                      {date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setShowAddPanel(true)} className="btn-primary flex items-center gap-2">
                    <Plus size={14} /> Add Stores
                  </button>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2">
                {visitsQuery.loading && visits.length === 0 ? (
                  <div className="text-center text-text3 text-sm py-12">Loading plan…</div>
                ) : visits.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center py-16">
                    <CalendarIcon size={42} className="text-text3 mb-3" />
                    <h4 className="text-sm font-bold text-text">No stores scheduled</h4>
                    <p className="text-xs text-text3 mt-1 max-w-xs">
                      Click <span className="font-bold">Add Stores</span> to start building today's plan.
                    </p>
                  </div>
                ) : (
                  displayedVisits.map((v, idx) => (
                    <PlanRow
                      key={v.id}
                      visit={v}
                      step={idx + 1}
                      dragging={draggingId === v.id}
                      onDragStart={() => onDragStart(v.id)}
                      onDragOver={(e) => onDragOver(e, v.id)}
                      onDragEnd={onDragEnd}
                      onTimeChange={(t) => handleTimeChange(v.id, t)}
                      onMarkDone={() => askMarkDone(v)}
                      onRemove={() => handleRemove(v.id)}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </main>
      </div>

      <SlidePanel
        isOpen={showAddPanel}
        onClose={() => setShowAddPanel(false)}
        width={480}
        title="Add Stores to Plan"
        subtitle={`${selectedFsr?.name ?? ''} · ${date.toLocaleDateString()}`}
        footer={
          <button
            onClick={handleAddSubmit}
            disabled={pickedRetailerIds.length === 0 || adding}
            className="btn-primary w-full disabled:opacity-60"
          >
            {adding
              ? 'Adding…'
              : pickedRetailerIds.length === 0
              ? 'Pick at least one store'
              : `Add ${pickedRetailerIds.length} ${pickedRetailerIds.length === 1 ? 'Store' : 'Stores'}`}
          </button>
        }
      >
        <AddStoresPanel
          existingRetailerIds={visits.map((v) => v.retailerId)}
          assignedRetailerIds={selectedFsr?.assignedRetailerIds ?? []}
          picked={pickedRetailerIds}
          onPickedChange={setPickedRetailerIds}
          time={pickedTime}
          onTimeChange={setPickedTime}
        />
      </SlidePanel>

      <Modal
        isOpen={pendingDone !== null}
        type="success"
        title="Mark visit as done?"
        message={
          pendingDone
            ? `${pendingDone.retailerName || 'This retailer'} will be marked complete for ${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}. Mobile FSR app sync will reflect this immediately.`
            : ''
        }
        confirmText="Mark Done"
        cancelText="Cancel"
        onConfirm={confirmMarkDone}
        onCancel={() => setPendingDone(null)}
      />
    </div>
  );
};

// ── Date Navigator ──────────────────────────────────────────────
const DateNavigator: React.FC<{ date: Date; onChange: (d: Date) => void }> = ({ date, onChange }) => {
  const today = new Date();
  const weekStart = startOfWeek(date);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="card p-4 flex items-center gap-4">
      <button
        onClick={() => onChange(addDays(date, -1))}
        className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-text2 flex items-center justify-center"
      >
        <ChevronLeft size={18} />
      </button>

      <div className="flex-1 flex items-center justify-between gap-2">
        {weekDays.map((d, i) => {
          const isSel = sameDay(d, date);
          const isToday = sameDay(d, today);
          return (
            <button
              key={i}
              onClick={() => onChange(d)}
              className={`flex-1 flex flex-col items-center justify-center py-2 rounded-lg transition-colors
                ${isSel ? 'bg-primary text-white shadow' : 'hover:bg-gray-50 text-text'}`}
            >
              <span className={`text-[10px] font-bold uppercase ${isSel ? 'text-white/80' : 'text-text3'}`}>
                {dayLabels[i]}
              </span>
              <span className="text-lg font-black">{d.getDate()}</span>
              {isToday && (
                <span className={`text-[8px] font-bold uppercase ${isSel ? 'text-white/80' : 'text-primary'}`}>
                  Today
                </span>
              )}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => onChange(addDays(date, 1))}
        className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-text2 flex items-center justify-center"
      >
        <ChevronRight size={18} />
      </button>

      <input
        type="date"
        value={toIso(date)}
        onChange={(e) => {
          if (!e.target.value) return;
          const [y, m, d] = e.target.value.split('-').map(Number);
          onChange(new Date(y, (m || 1) - 1, d || 1));
        }}
        className="h-10 px-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-bold text-text2 cursor-pointer focus:outline-none"
      />

      {!sameDay(date, today) && (
        <button
          onClick={() => onChange(today)}
          className="h-10 px-4 bg-primary text-white rounded-lg text-xs font-bold uppercase tracking-widest"
        >
          Today
        </button>
      )}
    </div>
  );
};

// ── KPI Card ────────────────────────────────────────────────────
const KpiCard: React.FC<{ label: string; value: number; icon: any; grad: string }> = ({
  label,
  value,
  icon: Icon,
  grad,
}) => (
  <div className="bg-white p-5 rounded-3xl border border-white shadow-sm flex items-center gap-4">
    <div
      className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center text-white shadow-lg`}
    >
      <Icon size={20} strokeWidth={2.5} />
    </div>
    <div>
      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">{label}</p>
      <div className="text-2xl font-black text-slate-900 tracking-tight leading-none">{value}</div>
    </div>
  </div>
);

// ── Plan Row ────────────────────────────────────────────────────
const PlanRow: React.FC<{
  visit: Visit;
  step: number;
  dragging: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onTimeChange: (t: string) => void;
  onMarkDone: () => void;
  onRemove: () => void;
}> = ({ visit, step, dragging, onDragStart, onDragOver, onDragEnd, onTimeChange, onMarkDone, onRemove }) => {
  const [time, setTime] = useState(visit.scheduledTime || '');

  useEffect(() => {
    setTime(visit.scheduledTime || '');
  }, [visit.scheduledTime]);

  const isDone = visit.status === 'done';
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all
        ${dragging ? 'opacity-40 border-dashed' : ''}
        ${isDone ? 'bg-emerald-50/50 border-emerald-200' : 'bg-white border-border hover:border-primary/30'}`}
    >
      <button className="cursor-grab active:cursor-grabbing text-text3 hover:text-text2 px-1" tabIndex={-1}>
        <GripVertical size={16} />
      </button>

      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-black text-xs shadow-sm shrink-0 ${STATUS_DOT[visit.status]}`}
      >
        {step}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold truncate ${isDone ? 'text-emerald-700 line-through' : 'text-text'}`}>
            {visit.retailerName || `Retailer ${visit.retailerId.slice(-6).toUpperCase()}`}
          </span>
          {visit.priority === 'high' && (
            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[9px] font-black rounded uppercase">High</span>
          )}
        </div>
        <div className="text-[11px] text-text3 truncate">
          {visit.retailerCity || '—'}
          {visit.retailerMobile ? ` · ${visit.retailerMobile}` : ''}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <div className="flex items-center gap-1 px-2 h-8 rounded-lg border border-border bg-gray-50">
          <Clock size={12} className="text-text3" />
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            onBlur={() => {
              if (time !== (visit.scheduledTime || '')) onTimeChange(time);
            }}
            className="bg-transparent text-xs font-semibold text-text2 w-[68px] outline-none"
          />
        </div>

        <span className={`status-pill ${STATUS_PILL[visit.status]} px-2`}>{visit.status}</span>

        {!isDone && (
          <button
            onClick={onMarkDone}
            className="h-8 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold flex items-center gap-1"
          >
            <Check size={12} /> Mark Done
          </button>
        )}

        <button
          onClick={onRemove}
          className="w-8 h-8 rounded-lg border border-border hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-text3 flex items-center justify-center"
          title="Remove"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// ── Empty State ─────────────────────────────────────────────────
const EmptyState: React.FC = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center p-16">
    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-text3 mb-4">
      <CalendarIcon size={32} />
    </div>
    <h3 className="text-base font-bold text-text">Pick an officer</h3>
    <p className="text-xs text-text3 max-w-xs mt-1">
      Choose a Field Sales Representative from the rail to view or build their plan.
    </p>
  </div>
);

const customerListToConnector = (list: any): ConnectorRetailer[] =>
  (list ?? []).map((c: any) => ({
    id: c.id,
    displayName: c.displayName ?? null,
    companyName: c.companyName ?? null,
    city: c.city ?? null,
    email: c.email ?? null,
    mobile: c.mobile ?? null,
  }));

// ── Add Stores Panel ────────────────────────────────────────────
const AddStoresPanel: React.FC<{
  existingRetailerIds: string[];
  assignedRetailerIds: string[];
  picked: string[];
  onPickedChange: (next: string[]) => void;
  time: string;
  onTimeChange: (next: string) => void;
}> = ({
  existingRetailerIds,
  assignedRetailerIds,
  picked,
  onPickedChange,
  time,
  onTimeChange,
}) => {
  const [search, setSearch] = useState('');

  const { store: myStore } = useMyStore();
  const isCustom = myStore?.storeType === 'custom';

  const PAGE_SIZE = 50;

  // RiEtail stores pick from the upstream connector; custom stores pick from
  // their locally-managed Retailer collection.
  const retailerQuery = useQuery(GET_RETAILERS, {
    variables: { page: 1, limit: PAGE_SIZE, search: search || undefined },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: isCustom,
  });
  const customRetailerQuery = useQuery(GET_CUSTOM_RETAILERS, {
    variables: { search: search || undefined, pageNo: 0, limit: 500 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !isCustom,
  });

  const retailers: ConnectorRetailer[] = useMemo(() => {
    if (isCustom) {
      return (customRetailerQuery.data?.getCustomRetailers?.items ?? []).map((c: any) => ({
        id: c.id,
        displayName: c.name,
        companyName: null,
        city: c.city || null,
        email: c.emailId || null,
        mobile: c.mobileNumber || null,
      }));
    }
    return customerListToConnector(retailerQuery.data?.getRetailers?.customers);
  }, [isCustom, retailerQuery.data, customRetailerQuery.data]);

  const assignedSet = useMemo(() => new Set(assignedRetailerIds), [assignedRetailerIds]);
  const visibleRetailers = useMemo(
    () => retailers.filter((r) => assignedSet.has(r.id)),
    [retailers, assignedSet]
  );

  const total = isCustom
    ? customRetailerQuery.data?.getCustomRetailers?.total ?? retailers.length
    : retailerQuery.data?.getRetailers?.total ?? 0;
  const activeLoading = isCustom ? customRetailerQuery.loading : retailerQuery.loading;
  // Some of the FSR's assigned retailers may not be in the loaded page yet.
  const missingAssigned = assignedRetailerIds.length - visibleRetailers.length;
  const hasMoreToFetch = !isCustom && retailers.length < total;
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMore = async () => {
    if (isCustom) return; // single-shot load for custom stores
    if (loadingMore || retailers.length >= total) return;
    setLoadingMore(true);
    try {
      const nextPage = Math.floor(retailers.length / PAGE_SIZE) + 1;
      await retailerQuery.fetchMore({
        variables: { page: nextPage, limit: PAGE_SIZE, search: search || undefined },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult?.getRetailers) return prev;
          const seen = new Set<string>();
          const merged = [
            ...(prev?.getRetailers?.customers ?? []),
            ...fetchMoreResult.getRetailers.customers,
          ].filter((c: any) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
          return { getRetailers: { ...fetchMoreResult.getRetailers, customers: merged } };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const toggle = (id: string) => {
    onPickedChange(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  };

  return (
    <div className="space-y-5">
      {/* Time slot */}
      <div>
        <label className="text-[10px] font-bold uppercase tracking-widest text-text3 mb-1.5 block">
          Default time slot <span className="font-medium text-text3 normal-case tracking-normal">(optional)</span>
        </label>
        <div className="relative">
          <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text3 pointer-events-none" />
          <input
            type="time"
            value={time}
            onChange={(e) => onTimeChange(e.target.value)}
            className="w-full h-11 pl-9 pr-3 bg-[#F8FAFC] border-[1.5px] border-border rounded-lg text-sm font-semibold text-text focus:border-primary focus:bg-white outline-none transition"
          />
        </div>
      </div>

      {/* Search */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Available stores</label>
          {picked.length > 0 && (
            <span className="px-2 py-0.5 bg-indigo-50 border border-indigo-100 text-primary text-[10px] font-black rounded-full">
              {picked.length} selected
            </span>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text3 pointer-events-none" size={14} />
          <input
            type="text"
            placeholder="Search by name, mobile…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 pl-9 pr-3 bg-[#F8FAFC] border-[1.5px] border-border rounded-lg text-sm focus:border-primary focus:bg-white outline-none transition"
          />
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {assignedRetailerIds.length === 0 ? (
          <div className="text-center text-text3 text-sm py-12 px-6">
            No retailers assigned to this officer yet.
            <div className="mt-1 text-[11px] text-text3">
              Assign retailers in <span className="font-bold">Retailer Assignment</span> first.
            </div>
          </div>
        ) : activeLoading && visibleRetailers.length === 0 ? (
          <div className="text-center text-text3 text-sm py-12">Loading assigned retailers…</div>
        ) : visibleRetailers.length === 0 ? (
          <div className="text-center text-text3 text-sm py-12">No matching retailers found.</div>
        ) : (
          visibleRetailers.map((r) => {
            const display = r.displayName || r.companyName || `Retailer ${r.id.slice(-6).toUpperCase()}`;
            const alreadyScheduled = existingRetailerIds.includes(r.id);
            const isSelected = picked.includes(r.id);
            return (
              <button
                key={r.id}
                disabled={alreadyScheduled}
                onClick={() => toggle(r.id)}
                className={`w-full flex items-center gap-3 p-3 pr-4 rounded-xl border text-left transition-all
                  ${isSelected ? 'border-primary bg-indigo-50/70 shadow-sm' : 'border-border bg-white hover:border-primary/40 hover:bg-gray-50/70'}
                  ${alreadyScheduled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-[11px] shrink-0 transition-colors
                    ${isSelected ? 'bg-primary text-white' : 'bg-emerald-500 text-white'}`}
                >
                  {initialsOf(display)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-text truncate">{display}</span>
                    {alreadyScheduled && (
                      <span className="text-[9px] font-black uppercase text-text3 bg-gray-100 rounded px-1.5 py-0.5 shrink-0">
                        Scheduled
                      </span>
                    )}
                  </div>
                  {(r.city || r.mobile) && (
                    <div className="text-[11px] text-text3 truncate mt-0.5">
                      {[r.city, r.mobile].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all
                    ${isSelected ? 'bg-primary border-primary' : 'border-border bg-white'}`}
                >
                  {isSelected && <Check size={14} className="text-white" strokeWidth={4} />}
                </div>
              </button>
            );
          })
        )}

        {hasMoreToFetch && missingAssigned > 0 && !activeLoading && (
          <div className="pt-3 flex flex-col items-center gap-1.5">
            <span className="text-[10px] font-bold text-text3">
              {missingAssigned} assigned {missingAssigned === 1 ? 'store' : 'stores'} not loaded yet
            </span>
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="h-9 px-4 rounded-full border border-border bg-white text-text2 font-bold text-xs hover:bg-gray-50 disabled:opacity-60 flex items-center gap-2"
            >
              {loadingMore ? (
                <>
                  <RefreshCw size={12} className="animate-spin" /> Loading…
                </>
              ) : (
                <>Load more</>
              )}
            </button>
          </div>
        )}
      </div>

    </div>
  );
};
